import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Group, Button, Container, Grid, GridCol, TextInput, Select, Accordion } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { IEntityActivity } from '../../../../libraries/entity';
import { LoginState } from '../../../client-redux/login/reducer';
import {
  IHelpdeskEntity,
  createHelpdeskEntity,
  fetchEntitiesActivity,
  fetchHelpdeskEntityInfo,
  updateHelpdeskEntity
} from '../../../../libraries/helpdesk/entities';
import ChooseEntityModal from '../../components/choose-entity-modal';
import DeleteHeldeskEntity from './delete-entity';
import Input from '../../../components/inputs';
import Header from '../../../components/util/header';
import { useStatusOperDialog } from '../../../context/status-oper-dialog';
import { StatusType } from '../../../../libraries/zps';
import './styles.scss';

enum SELECTED_FIELD {
  GID,
  ENTITY,
  BILLING
}

enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

const HelpdeskEntity: React.FC = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const languages = useSelector<AppState, any[]>(state => state.lang.langSettings || []);
  const regions = useSelector<AppState, string[]>(state => state.regions.regions);
  const navigate = useNavigate();

  const [activities, setActivities] = useState<IEntityActivity[]>([]);
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const controller = useRef(new AbortController()).current;
  const [entity, setEntity] = useState<IHelpdeskEntity[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<SELECTED_FIELD>(SELECTED_FIELD.GID);

  const { mode: modeParam, entid } = useParams();

  const location = useLocation();
  const [, setReloadPage] = useState(false);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  const entityTypes = [
    { value: '0', label: lang.ENTITY_TYPE_0 },
    { value: '1', label: lang.ENTITY_TYPE_1 },
    { value: '2', label: lang.ENTITY_TYPE_2 },
    { value: '3', label: lang.ENTITY_TYPE_3 }
  ];

  const mode = useMemo(() => {
    switch (modeParam) {
      case MODE.NEW:
      case MODE.EDIT:
      case MODE.READ:
        return modeParam as MODE;
      default:
        return MODE.READ;
    }
  }, [modeParam]);

  useEffect(() => {
    if (mode !== MODE.NEW && entid) {
      getEquipment();
    } else if (mode === MODE.NEW) {
      form.reset();
    }

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, entid]);

  useEffect(() => {
    const path = location.pathname;
    const editable = /\/helpdesk\/entity\/(edit|new\/\d+)/.test(path);
    setIsEditable(editable);
  }, [location]);

  useEffect(() => {
    fetchEntitiesActivity().then(response => {
      setActivities(response);
    });
  }, []);

  const getEquipment = () => {
    if (!loginInfo.token) return;
    if (mode === MODE.NEW) return;

    fetchHelpdeskEntityInfo(Number(entid), controller)
      .then(response => {
        const statusOper = response.data?.statusOper;
        if (!statusOper || statusOper.code !== 0) {
          showStatusOperDialog({
            type: StatusType.ERROR,
            message: statusOper?.message || lang.ALERT_ERROR_FETCHING_DATA,
            statusOper
          });
          return;
        }
        setEntity(response.data.entities || []);
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          handleApiError(error, lang.ALERT_ERROR_FETCHING_DATA);
        }
      });
  };

  const form = useForm<IHelpdeskEntity>({
    initialValues: {
      activity: 0,
      address: '',
      billingEntity: 0,
      billingVolume: 0,
      contact: '',
      description: '',
      email: '',
      entid: undefined,
      gid: undefined,
      lang: 0,
      logo: '',
      mobile: '',
      name: '',
      nif: 0,
      ownerid: 0,
      postalCode: '',
      printType: 0,
      region: '',
      reportType: 0,
      sibsMerchantCode: '',
      type: 0,
      usePos: 0,
      useShifts: 0,
      maxCurrencies: 1
    }
  });

  useEffect(() => {
    if (!entity || entity.length === 0) return;
    const formEntity = entity[0];

    form.setValues({
      ...form.values,
      activity: formEntity?.activity || 0,
      address: formEntity?.address || '',
      billingEntity: formEntity?.billingEntity || 0,
      billingVolume: formEntity?.billingVolume || 0,
      contact: formEntity?.contact || '',
      description: formEntity?.description || '',
      email: formEntity?.email || '',
      entid: Number(entid) || undefined,
      gid: formEntity?.gid || undefined,
      lang: formEntity?.lang || 0,
      logo: formEntity?.logo || '',
      mobile: formEntity?.mobile || '',
      name: formEntity?.name || '',
      nif: formEntity?.nif || 0,
      ownerid: formEntity?.ownerid || 0,
      postalCode: formEntity?.postalCode || '',
      printType: formEntity?.printType || 0,
      region: formEntity?.region || '',
      reportType: formEntity?.reportType || 0,
      sibsMerchantCode: formEntity?.sibsMerchantCode || '',
      type: formEntity?.type || 0,
      usePos: formEntity?.usePos || 0,
      useShifts: formEntity?.useShifts || 0,
      maxCurrencies: formEntity?.maxCurrencies || 1
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const submitForm = async (formValues: any) => {
    const request: IHelpdeskEntity = {
      activity: formValues.activity,
      address: formValues.address,
      billingEntity: formValues.billingEntity,
      billingVolume: formValues.billingVolume,
      contact: formValues.contact,
      description: formValues.description,
      email: formValues.email,
      entid: Number(formValues.entid),
      gid: formValues.gid,
      lang: formValues.lang,
      logo: formValues.logo,
      mobile: formValues.mobile,
      name: formValues.name,
      nif: formValues.nif,
      ownerid: formValues.ownerid,
      postalCode: formValues.postalCode,
      printType: formValues.printType,
      region: formValues.region,
      reportType: formValues.reportType,
      sibsMerchantCode: formValues.sibsMerchantCode,
      type: formValues.type,
      usePos: formValues.usePos,
      useShifts: formValues.useShifts,
      maxCurrencies: formValues.maxCurrencies
    };

    const formController = new AbortController();

    try {
      const response =
        mode === MODE.NEW
          ? await createHelpdeskEntity(request, formController)
          : await updateHelpdeskEntity(request, formController);

      const statusOper = response.data?.statusOper;

      if (!statusOper || statusOper.code !== 0) {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.EDIT_EQUIPMENTS_ALERT_UPDATED_ERROR,
          statusOper
        });
        return;
      }

      const successMessage =
        mode === MODE.NEW
          ? lang.EDIT_EQUIPMENTS_ALERT_UPDATED_SUCCESS || 'Entity created successfully'
          : lang.EDIT_EQUIPMENTS_ALERT_UPDATED_SUCCESS || 'Entity updated successfully';

      showStatusOperDialog({
        type: StatusType.INFO,
        message: successMessage,
        statusOper
      });

      if (mode === MODE.NEW && response.data?.entid) {
        setTimeout(() => {
          navigate(`/helpdesk/entity/edit/${response.data.entid}`);
        }, 500);
      } else {
        getEquipment();
      }
    } catch (error) {
      handleApiError(
        error,
        mode === MODE.NEW ? lang.EDIT_EQUIPMENTS_ALERT_UPDATED_ERROR : lang.EDIT_EQUIPMENTS_ALERT_UPDATED_ERROR
      );
    }
  };

  const chooseEntid = (entid: number) => {
    switch (selectedField) {
      case SELECTED_FIELD.GID:
        form.setValues({ gid: entid });
        break;
      case SELECTED_FIELD.BILLING:
        form.setValues({ billingEntity: entid });
        break;
      case SELECTED_FIELD.ENTITY:
        form.setValues({ entid: undefined });
        break;
      default:
        break;
    }
  };

  const toggleChooseEntityModal = () => {
    setOpenModal(!openModal);
    setSelectedField(SELECTED_FIELD.GID);
  };

  const toggleChooseBillingModal = () => {
    setOpenModal(!openModal);
    setSelectedField(SELECTED_FIELD.BILLING);
  };

  const handleReload = () => {
    setReloadPage(true);
    setTimeout(() => {
      setOpenModal(false);
      window.location.href = `/helpdesk/entities/`;
    }, 2000);
  };

  return (
    <Container fluid classNames={{ root: mode === MODE.NEW ? 'helpdesk-entity-new' : 'helpdesk-entity-read' }}>
      <Header pageTitle={entity ? `${lang.GLOBAL_ENTITY}: ${entid}` : lang.ENTITY_NEW_ENTITY} />

      {lang && (
        <ChooseEntityModal
          lang={lang}
          isOpen={openModal}
          hideBtn={() => toggleChooseEntityModal()}
          chooseEntid={chooseEntid}
        />
      )}

      <form
        onSubmit={form.onSubmit(values => {
          submitForm(values);
        })}
      >
        {/* Gid */}
        <Grid gap="lg">
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <Input
              label={lang.EDIT_ENTITY_FORM_LABEL_GID}
              placeholder={lang.GLOBAL_OPTIONAL}
              value={form.values.gid ?? ''}
              disabled={!isEditable}
              isEditable={isEditable}
              search={isEditable ? toggleChooseEntityModal : undefined}
              onClick={isEditable ? toggleChooseEntityModal : undefined}
              onChange={e => {
                const val = e.target.value === '' ? undefined : Number(e.target.value);
                form.setValues({ ...form.values, gid: val });
              }}
            />
          </GridCol>

          {/* Entid */}
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <TextInput
              label={lang.GLOBAL_ID_ENTITY}
              placeholder={lang.ENTITY_AUTO_ENTID}
              value={form.values.entid ?? ''}
              onChange={e => {
                const val = e.target.value === '' ? undefined : Number(e.target.value);
                form.setValues({ ...form.values, entid: val });
              }}
            />
          </GridCol>

          {/* Type */}
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <Select
              label={lang.GLOBAL_TYPE}
              placeholder={mode === MODE.NEW ? lang.GLOBAL_TYPE : ''}
              {...form.getInputProps('type')}
              data={[
                ...(entityTypes?.map(entityType => ({
                  value: entityType.value.toString(),
                  label: entityType.label
                })) || [])
              ]}
              value={form.values.type?.toString()}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          {/* Name */}
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <TextInput
              label={lang.GLOBAL_NAME}
              placeholder={mode === MODE.NEW ? lang.GLOBAL_NAME : ''}
              {...form.getInputProps('name')}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          {/* Description */}
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <TextInput
              label={lang.EQUIPMENT_DESCRIPTION}
              placeholder={mode === MODE.NEW ? lang.EQUIPMENT_DESCRIPTION : ''}
              {...form.getInputProps('description')}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          {/* Mobile */}
          <GridCol span={6}>
            <TextInput
              label={lang.USER_MOBILE}
              placeholder={mode === MODE.NEW ? lang.USER_MOBILE : ''}
              {...form.getInputProps('mobile')}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          {/* Cell phone */}
          <GridCol span={6}>
            <TextInput
              label={lang.USER_PHONE}
              placeholder={mode === MODE.NEW ? lang.USER_PHONE : ''}
              {...form.getInputProps('contact')}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          {/* Email */}
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <TextInput
              label="Email"
              placeholder={mode === MODE.NEW ? 'Email' : ''}
              {...form.getInputProps('email')}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          {/* Address */}
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <TextInput
              label={lang.GLOBAL_ADDRESS}
              placeholder={mode === MODE.NEW ? lang.GLOBAL_ADDRESS : ''}
              {...form.getInputProps('address')}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          {/* Zip code */}
          <GridCol span={{ base: 12, md: 6, lg: 4 }}>
            <TextInput
              label={lang.EVENT_ZIP_CODE}
              placeholder={mode === MODE.NEW ? lang.EVENT_ZIP_CODE : ''}
              {...form.getInputProps('postalCode')}
              disabled={mode === MODE.READ}
            />
          </GridCol>

          <Accordion variant="unstyled" radius="md" className="accordion">
            <Accordion.Item value="additional-info">
              <Accordion.Control>{lang.GLOBAL_ADDITIONAL_INFORMATION}</Accordion.Control>
              <Accordion.Panel>
                <Grid className="additional-info-grid">
                  {/* Language */}
                  <GridCol span={6}>
                    <Select
                      label={lang.USER_LANG}
                      placeholder={mode === MODE.NEW ? lang.USER_LANG : ''}
                      {...form.getInputProps('lang')}
                      value={form.values.lang.toString()}
                      defaultValue=""
                      data={[
                        ...(languages?.map(language => ({
                          value: language.langid.toString(),
                          label: language.nativeName
                        })) || [])
                      ]}
                      searchable
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Region */}
                  <GridCol span={6}>
                    <Select
                      label={lang.USER_REGION}
                      placeholder={mode === MODE.NEW ? lang.USER_REGION : ''}
                      {...form.getInputProps('region')}
                      defaultValue=""
                      data={[
                        ...(regions?.map(region => ({
                          value: region,
                          label: region
                        })) || [])
                      ]}
                      searchable
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Owner ID */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EDIT_ENTITY_FORM_LABEL_OWNERID}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_OWNERID : ''}
                      {...form.getInputProps('ownerid')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Activity branch */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <Select
                      label={lang.EDIT_ENTITY_FORM_LABEL_ACTIVITY}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_ACTIVITY : ''}
                      {...form.getInputProps('activity')}
                      value={form.values.activity?.toString() || ''}
                      data={[
                        { value: '', label: lang.EDIT_ENTITY_FORM_LABEL_ACTIVITY_NOT_DEFINED },
                        ...(activities?.map(activity => ({
                          value: activity?.id.toString(),
                          label: activity?.description
                        })) || [])
                      ]}
                      searchable
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Sibs Mech. Code */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EDIT_ENTITY_FORM_LABEL_SIBSMERCHANTCODE}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_SIBSMERCHANTCODE : ''}
                      {...form.getInputProps('sibsMerchantCode')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Billing entity */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <Input
                      label={lang.EDIT_ENTITY_FORM_LABEL_BILLINGENTITY}
                      value={form.values.billingEntity ?? ''}
                      disabled={!isEditable}
                      isEditable={isEditable}
                      search={isEditable ? toggleChooseBillingModal : undefined}
                      onClick={isEditable ? toggleChooseBillingModal : undefined}
                      {...form.getInputProps('billingEntity')}
                    />
                  </GridCol>

                  {/* NIF */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label="NIF"
                      placeholder={mode === MODE.NEW ? 'NIF' : ''}
                      {...form.getInputProps('nif')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Billing volume */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EDIT_ENTITY_FORM_LABEL_BILLVOLUME}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_BILLVOLUME : ''}
                      {...form.getInputProps('billingVolume')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Max currencies */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EQUIPMENT_MAX_CURRENCIES}
                      value={form.values.maxCurrencies}
                      {...form.getInputProps('maxCurrencies')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Shifts */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EDIT_ENTITY_FORM_LABEL_USESHIFTS}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_USESHIFTS : ''}
                      {...form.getInputProps('useShifts')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* POS */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label="POS"
                      placeholder={mode === MODE.NEW ? 'POS' : ''}
                      {...form.getInputProps('usePos')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Print type */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EDIT_ENTITY_FORM_LABEL_PRINTTYPE}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_PRINTTYPE : ''}
                      {...form.getInputProps('printType')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Report type */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EDIT_ENTITY_FORM_LABEL_REPORTTYPE}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_REPORTTYPE : ''}
                      {...form.getInputProps('reportType')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>

                  {/* Logo */}
                  <GridCol span={{ base: 12, md: 6, lg: 4 }}>
                    <TextInput
                      label={lang.EDIT_ENTITY_FORM_LABEL_LOGO}
                      placeholder={mode === MODE.NEW ? lang.EDIT_ENTITY_FORM_LABEL_LOGO : ''}
                      {...form.getInputProps('logo')}
                      disabled={mode === MODE.READ}
                    />
                  </GridCol>
                </Grid>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {/* Buttons */}
          <Group className="submit-save-buttons">
            {mode !== MODE.READ && (
              <Button radius="md" type="submit" color="#538539">
                {mode === MODE.NEW ? `${lang.GLOBAL_SAVE_BUTTON}` : `${lang.GLOBAL_UPDATE}`}
              </Button>
            )}

            {mode === MODE.EDIT && <DeleteHeldeskEntity entity={Number(entid)} onReload={handleReload} />}
          </Group>
        </Grid>
      </form>
    </Container>
  );
};

export default HelpdeskEntity;
